/**
 * Created by vsuthar on 9/20/21
 * File: GlobalActions
 * Project: Cloud Management Module ©Palo Alto Networks
 * © Palo Alto Networks
 */
import { groupBy, sortedUniq } from 'lodash'
import {
  CLAIM_ERROR_MSG,
  DUAL_NETWORKS_APP_IDS,
  SKU_SERVICE_LOCATION_LOCAL, SKU_SERVICE_LOCATION_WORLDWIDE,
  SUBSCRIPTION_ERROR_MAPPING, VALIDATION_FAILED, VALIDATION_SUCCESS
} from '../../subscription/components/constants/SubscriptionConstant'
import SubscriptionService from '../../subscription/services/SubscriptionService'
import ActivationSubscriptionService from '../../activation/services/SubscriptionService'
import InstanceService from '../../tenancy/services/InstanceService'
import TenancyService from '../../tenancy/services/TenancyService'
import {
  APP_ID_DSS_ID,
  APP_ID_LOGGING_SERVICE, APP_ID_PAN_CHINA,
  APP_ID_PRISMA_ACCESS,
  APP_ID_PRISMA_ACCESS_EDITION,
  APP_ID_PRISMA_ACCESS_EDITION_ONPREM,
  APP_ID_PRIVATE_APPS,
  APP_ID_PRIVATE_APPS_QTY,
  APP_ID_SDWAN,
  APP_ID_SERVICE_LOC,
  TSG_NONE
} from '../constants/GlobalConstants'
import IAMService from '../../iam/services/IAMService'
import { getTsgToken, actions } from '@sparky/framework'
import { APP_ID_TO_NAME, DO_NOT_DISPLAY_ADDONS } from '../constants/LabelConstants'
import {
  loadApps
} from '../reducers/actions/AppReducer.actions'
import {
  loadCSPAccounts,
  loadLicense,
  loadInstances,
  setLicenseMessage,
} from '../reducers/actions/LicenseReducer.actions'
import {
  loadCSPDevices,
  loadDevices
} from '../reducers/actions/DeviceReducer.actions'
import {
  loadTreeData,
  resetTreeData,
  loadAcquisitions,
  loadAcqRequests, setAcqLoading
} from '../reducers/actions/TreeReducer.actions'
import {
  setIsTenantMGMLoading,
  setTenantSwitcherLoading
} from '../reducers/actions/ViewReducer.actions'
import {
  setAccessPolicies,
  setPermissions,
  setRoles,
  setPermissionSets,
  setCustomRoles,
  setServiceAccounts,
} from '../reducers/actions/IAMReducer.actions'
import {
  setInstances
} from '../reducers/actions/InstanceReducer.actions'
import { noop, pick } from 'lodash'
import { getVarValue, getAuthState, loadTsgInstances } from '../utils/Sparky'
import { FF_SHOW_SUBSCRIPTIONS } from '../utils/FeatureFlags'
import TransFormLicenseResponse from '../utils/TransFormLicenseResponse'

const showSubscriptions = getVarValue(FF_SHOW_SUBSCRIPTIONS, false)

export const LoadTreeData = (dispatch) => {
  actions.loadTsgTree(TSG_NONE.id).then((tsgs) => {
    setTimeout(() => { // need some delay
      if (Array.isArray(tsgs.children) && !tsgs.error) {
        dispatch(loadTreeData(Array.from(tsgs.children)))
      }
      else {
        dispatch(resetTreeData())
      }
    }, 100)
  }).catch((err) => {
    dispatch(loadTreeData([]))
  })
}

export const LoadTreeDataAsync = (dispatch) => {
  return new Promise((resolve, reject) => {
    actions.loadTsgTree(TSG_NONE.id).then((tsgs) => {
      setTimeout(() => { // need some delay
        if (Array.isArray(tsgs.children) && !tsgs.error) {
          dispatch(loadTreeData(Array.from(tsgs.children)))
          resolve('Success')
        }
        else {
          dispatch(resetTreeData())
          resolve('Success')
        }
      }, 100)
    }).catch((err) => {
      dispatch(loadTreeData([]))
      reject('Failed')
    })
  })

}

export const LoadInstanceData = async (dispatch, param, actionType) => {
  if (showSubscriptions) {
    SubscriptionService.instances.list(param).then(({ data }) => {
      dispatch({
        type: actionType,
        instances: data
      })
    }).catch((err) => {
      dispatch({
        type: actionType,
        instances: []
      })
    })
  }
  else {
    InstanceService.listInstance.get(param).then(({ data }) => {
      dispatch({
        type: actionType,
        instances: data
      })
    }).catch((err) => {
      dispatch({
        type: actionType,
        instances: []
      })
    })
  }
}

export const UpdateGlobalTsgID = async (dispatch, contextTsgID, actionType) => {
  dispatch({
    type: actionType,
    tsgId: contextTsgID
  })
}

export const LoadAccessTokens = async (tsgIds) => {
  return new Promise((resolve, reject) => {
    const tokens = []
    for (let index = 0; index < tsgIds.length; index++) {
      tokens[index] = callForAccessToken(tsgIds[index])
    }
    Promise.all(tokens).then(values => {
      values && values?.length > 0 ? resolve(Object.assign(...values)) : resolve([])
    }).catch(error => {
      reject(error)
    })
  })
}

const callForAccessToken = (tsgId) => {
  return getTsgToken(tsgId).then((data) => {
    const resp = {}
    resp[tsgId] = data
    return resp
  }, () => ({}))
}

export const LoadAllInstanceData = async (tsgIds, tsg_id, dispatch) => {
  const results = await Promise.allSettled(tsgIds.map(tsgId => loadTsgInstances(tsgId)))
    .then(results => results.filter(result => result.status === 'fulfilled' && result.value).flatMap(({ value }) => {
      if (value?.error && tsgIds.length === 1 && tsgIds[0] === tsg_id) {
        throw value.error
      }
      return value
    }))
  dispatch(setIsTenantMGMLoading(false))
  return results
}

/*
const invokeInstanceApi = (tsgIds, token) => {
  return new Promise((resolve, reject) => {
    InstanceService.init(token)
    const param = { 'tsg_id': tsgIds }
    InstanceService.listInstance.get(param).then((resp) => {
      resolve(resp.data)
    },
    (err) => {
      resolve([])
    })
  })
}
*/

export const isTenantMgmLoading = (isLoading, dispatch) => {
  dispatch(setIsTenantMGMLoading(isLoading))
}

export const makeTenantSwitcherLoad = (dispatch, isTenantSwitcherLoading) => {
  dispatch(setTenantSwitcherLoading(isTenantSwitcherLoading))
}

export const LoadLicense = (params, dispatch, setLicenseLoaded = noop) => {
  SubscriptionService.listLicense.get(params).then(({ data }) => {
    const { message, response } = (data.de_hash || data.claimed_license) ? TransFormLicenseResponse(data) : {}
    dispatch(loadLicense(response ?? data))
    dispatch(setLicenseMessage(message))
    typeof setLicenseLoaded === 'function' && setLicenseLoaded(true)
  }).catch((err) => {
    dispatch(loadLicense([]))
    dispatch(setLicenseMessage(err?.message ? err?.message : 'Error Fetching License Information', true))
    typeof setLicenseLoaded === 'function' && setLicenseLoaded(true)
  })
}


export const LoadInstances = ({ tsg_id }, dispatch) => {
  return new Promise((resolve, reject) => {
    if (showSubscriptions) {
      loadTsgInstances(tsg_id).then(instances => {
        dispatch(loadInstances(instances))
        dispatch(setInstances(instances))
        resolve(instances)
      }).catch(error => {
        dispatch(loadInstances([]))
        dispatch(setInstances([]))
        resolve([])
      })
    }
    else {
      InstanceService.listInstance.get(tsg_id, { tsg_id }).then(({ data }) => {
        const instances = Array.isArray(data) ? data : data.items
        dispatch(loadInstances(instances))
        dispatch(setInstances(instances))
        resolve(instances)
      }).catch(error => {
        dispatch(loadInstances([]))
        dispatch(setInstances([]))
        resolve([])
      })
    }
  })
}

// export const LoadUserInstances = async (tsgs, dispatch) => {
//   await InstanceService.getInstances.get(tsgs).then(instances => {
//     dispatch(loadUserInstances(instances))
//   })
// }

export const LoadCSPAccounts = (params, dispatch, isFramework) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.listCspAccounts.get(params).then(({ data }) => {
      dispatch(loadCSPAccounts(data, isFramework))
      resolve(data)
    }).catch((err) => {
      dispatch(loadCSPAccounts([]))
      resolve([])
    })
  })
}

export const ClaimLicenses = (params, tsg_id) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.listLicense.post(params, tsg_id).then(({ data }) => {
      resolve({ status: SUBSCRIPTION_ERROR_MAPPING['GENERIC_SUCCESS'], response: data })
    }, (err) => {
      reject({ message: err.message ?? CLAIM_ERROR_MSG })
    })
  })
}
export const ReClaimLicenses = (params, tsg_id) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.reClaim.put(params, tsg_id).then(({ data }) => {
      resolve({ status: SUBSCRIPTION_ERROR_MAPPING['GENERIC_SUCCESS'], response: data })
    }, (err) => {
      reject({ status: SUBSCRIPTION_ERROR_MAPPING[err.message] })
    })
  })

}

export const GetClaimedInfo = (tsg_id, ancestor) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.listLicenseForTsg.get({ tsg_id, ancestor }).then(({ data }) => {
      const isSDWANPresent = data.some((each) => {
        return each.app_id === APP_ID_SDWAN
      })
      const isPaePresent = data.some((each) => {
        let hasPae = false
        if (each.app_id === APP_ID_PRISMA_ACCESS_EDITION) {
          hasPae = each.parent_remaining.some((elem) => {
            return elem.app_id === APP_ID_PRISMA_ACCESS_EDITION
          })
        }
        return hasPae
      })
      resolve({ isSDWANPresent, isPaePresent, isClaimed: data.length > 0 })

    }, (err) => {
      resolve({ isSDWANPresent: false, isClaimed: false, isPaePresent: false })
    })
  })
}

export const GetLicenseDetails = (props) => {
  const { tsg_id, ancestor, isEdit, licenseInfo, instance } = props
  const licenseInfoByKey = {}
  isEdit && licenseInfo[APP_ID_PRISMA_ACCESS_EDITION]?.entitlements.forEach(each =>
    licenseInfoByKey[each.app_id.concat(each.license_type)] = each)
  return new Promise((resolve, reject) => {
    const result = {}
    const contracts = []
    SubscriptionService.listLicenseForTsg.get({ tsg_id, ancestor }).then(({ data }) => {
      const cgxProduct = []
      const addOns = []
      const cgx = data.find((each) => each.app_id === APP_ID_SDWAN)
      const pae = data.filter((each) => (each.app_id === APP_ID_PRISMA_ACCESS_EDITION || each.app_id === APP_ID_PRISMA_ACCESS_EDITION_ONPREM))
      if (cgx) {
        const licenseCardInfo = {
          licenseId: cgx.license_id,
          licenseDisplayName: cgx.app_id,
          //expiry: getDateFormat(elem.claim_at)
        }
        const cgxRequest = {
          'license_id': cgx.license_id,
          'eula_accepted': true,
          'tsg_name': '',
          'app_id': cgx.app_id,
          'platform': 'cloud',
          'region': '',
          'allocation_type': 'FULL',
          'instances': [
            {
              'app_id': 'cgx',
              'tenant_id': 'new'
            }
          ]
        }
        const data = {
          licenseCardInfo: licenseCardInfo,
          addOns: addOns,
          cgxRequest
        }
        cgxProduct.push(data)
      }
      pae.forEach((elem) => {
        const addOns = []
        let isGlobal = false
        const products = []
        const productCount = []
        const bundledApps = {}
        const cdl = []
        const appList = []
        const serviceConnection = []
        const serviceLocation = []
        const directorySync = []
        let isPanChina = false
        const validationRequest = []
        if (checkCountOfLicense(elem, isEdit) && elem.app_id !== APP_ID_SDWAN) {
          const fieldNameByKey = groupBy(elem['parent_remaining'], 'app_id')
          const elementByBundleId = groupBy(elem['parent_remaining'], 'bundle_display_name')
          const parentRemaining = elem['parent_remaining'].filter(each => !each['bundle_display_name'])
          directorySync.push({
            'app_id': APP_ID_DSS_ID,
            'product_type': 'PA-ADD',
            'license_type': 'PA-ADD',
            'purchased_size': 1,
            'display': true,
            'checkBox': elem.license_id.concat(':', 'directory_sync:checkBox'),
            'formFieldName': elem.license_id.concat(':', 'directory_sync:formFiled')
          })
          parentRemaining.forEach((res) => {
            if (res && res.app_id) {
              const validationPayload = pick(res, ['app_id', 'product_type', 'license_type', 'purchased_size'])
              res.formFieldName = elem.license_id.concat(':', elem.app_id, ':', res.app_id, ':', res.product_type)
              res.formFieldName = (fieldNameByKey[res.app_id] && fieldNameByKey[res.app_id].length > 1) ?
                res.formFieldName.concat(':', res.license_type) : res.formFieldName
              res.checkBox = res.formFieldName.concat('checkBox')
              res.support_account_id = elem.support_account_id
              appList.push(res.app_id === APP_ID_PRISMA_ACCESS_EDITION ? APP_ID_PRISMA_ACCESS : res.app_id)
              switch (res.app_id) {
                case APP_ID_PRIVATE_APPS: {
                  validationRequest.push(validationPayload)
                  res.display = true
                  serviceConnection.push(res)
                  break
                }
                case APP_ID_PRIVATE_APPS_QTY: {
                  validationRequest.push(validationPayload)
                  res.display = true
                  serviceConnection.push(res)
                  break
                }
                case APP_ID_SERVICE_LOC: {
                  validationRequest.push(validationPayload)
                  res.display = true
                  serviceLocation.push(res)
                  break
                }
                case APP_ID_LOGGING_SERVICE: {
                  res.display = true
                  cdl.push(res)
                  break
                }
                default: {
                  if (res.app_id === APP_ID_PAN_CHINA) {
                    isPanChina = true
                  }
                  if (res.product_type.includes('-ADD')) {
                    // if fedramp addon and PA china, skip adding to addons
                    if (DO_NOT_DISPLAY_ADDONS.has(res.app_id)) {
                      break
                    }
                    validationRequest.push(validationPayload)
                    res.display = true
                    addOns.push(res)
                  }
                  else {
                    isGlobal = res.license_type.toUpperCase().includes('GBL')
                    res.display = true
                    const purchasedSize = isEdit ? licenseInfoByKey[res.app_id.concat(res.license_type)]?.purchased_size : res.purchased_size
                    productCount.push(getUserCount(res.license_type, purchasedSize))
                    validationRequest.push(validationPayload)
                    products.push(res)
                  }
                }
              }
            }
          })
          const bundleKeys = Object.keys(elementByBundleId)?.filter((each) => each !== 'undefined')
          bundleKeys && bundleKeys.forEach((each) => {
            // To show only the unique App_id for CASB Bundle
            const uniqueElementsIds = []
            const uniqueElements = elementByBundleId[each].filter((item) => {
              if (uniqueElementsIds.includes(item.app_id)) {
                return false
              }
              appList.push(item.app_id)
              uniqueElementsIds.push(item.app_id)
              return true
            })
            getBundledInformation({
              bundleElements: uniqueElements,
              bundleName: each,
              validationRequest,
              support_account_id: elem.support_account_id,
              license_id: elem.license_id,
              app_id: elem.app_id,
            })
            bundledApps[each] = uniqueElements
          })
          const { serial_number, license_expiration } = getSerialNoAndExpiryDate(elem.serial_number, instance, isEdit, elem.license_expiration)
          const contract = {
            value: `${serial_number} : ${elem.application_display_name ?? APP_ID_TO_NAME[elem.app_id]} : ${getDateFormat(license_expiration)}`,
            description: elem.application_display_name ?? APP_ID_TO_NAME[elem.app_id],
            id: elem.license_id
          }
          const { keys, addOnsGrouped } = addOnsGroupBy(addOns)
          //const filteredKeys = keys?.filter((each) => each !== APP_ID_CUSTOMER_SUCCESS)
          const licenseCardInfo = {
            licenseDisplayName: elem.app_id,
            isPanChina,
            licenseQuantity: productCount.join(', '),
            serviceLocation: isGlobal ? SKU_SERVICE_LOCATION_WORLDWIDE : SKU_SERVICE_LOCATION_LOCAL,
            addOns: keys.concat(bundleKeys ?? []),
            expiry: getDateFormat(license_expiration)
          }
          // bundle.forEach((elem) => {
          //   elem.serviceLocation = isGlobal ? SKU_SERVICE_LOCATION_WORLDWIDE : SKU_SERVICE_LOCATION_LOCAL
          // })
          const allocationObj = {
            formFieldName: elem.license_id.concat(':', elem.app_id, ':', 'allocation'),
            value: true
          }
          const data = {
            licenseCardInfo,
            cdl,
            addOns: [...addOnsGrouped],
            products,
            appList: sortedUniq(appList),
            serviceConnection,
            serviceLocation,
            validationRequest,
            bundledApps,
            directorySync,
            allocation: allocationObj,
            allocationType: elem.allocation_type,
            support_account_id: elem.support_account_id
          }
          contracts.push(contract)
          result[elem.license_id] = [data]
        }
      })
      const selectContract = {
        value: contracts.length ? 'Choose Contract To Proceed Allocation' : 'No Contract Available For Allocation',
        description: contracts.length ? 'Choose Contract To Proceed Allocation' : 'No Contract Available For Allocation',
        id: ''
      }
      contracts.unshift(selectContract)
      contracts.length > 0 ? resolve({ contracts: contracts, products: result, response: data, cgxProduct }) : resolve({})
    }).catch((err) => {
      reject({})
    })

  })
}
/*
 * To ge the serial number and expiration data from the instance store, if it is not present in claims
 * For migrated and single tenant tsg
 */
const getSerialNoAndExpiryDate = ((serial_number, instance, isEdit, license_expiration) => {
  if (serial_number && license_expiration) {
    return { serial_number, license_expiration }
  }
  const paeInstance = instance.find(({ app_id }) => app_id === APP_ID_PRISMA_ACCESS_EDITION)
  if (isEdit && instance && paeInstance) {
    return { serial_number: paeInstance.serial_number, license_expiration: paeInstance.license_expiration }
  }
  return {}
})
const getBundledInformation = (props) => {
  const { bundleElements, bundleName, license_id, app_id, support_account_id, validationRequest } = props
  bundleElements.forEach((res) => {
    res.display = true
    res.support_account_id = support_account_id
    const validationPayload = pick(res, ['app_id', 'product_type', 'license_type', 'purchased_size'])
    validationRequest.push(validationPayload)
    res.formFieldName = license_id.concat(':', app_id, ':', res.app_id, ':', res.product_type, ':', bundleName)
    res.checkBox = res.formFieldName.concat('checkBox')
    if (bundleElements[bundleName]?.length) {
      res.formFieldName = res.formFieldName.concat(':', res.license_type)
    }
  })
}
const checkCountOfLicense = ((licenseDetails, isEdit) => {
  if (isEdit) {
    return true
  }
  const result = licenseDetails.parent_remaining ? licenseDetails.parent_remaining.some((each) => {
    //Added this includes to fix an issue in the UAT where  app_id==prisma_access_for_mobile_users for NFR
    return (each.app_id.includes('prisma_access') && each.purchased_size > 0)
  }) : false
  return result
})
// const bundledLicense = (res, expiry) => {
//   const addOns = []
//   let isGlobal = false
//   const productCount = []
//   const validationRequest = []
//   if (res.add_ons && res.add_ons.length > 0) {
//     addOns.push(...res.add_ons)
//     validationRequest.push(...res.add_ons)
//   }
//   //delete res.add_ons
//   const productInfo = [{ ...res }]
//   const { keys, addOnsGrouped } = addOnsGroupBy(addOns)
//   productInfo.forEach((elem) => {
//     if (elem.license_type.includes('GBL')) {
//       isGlobal = true
//     }
//     productCount.push(getUserCount(elem.license_type, elem.purchased_size))
//   })
//   const licenseCardInfo = {
//     licenseDisplayName: productInfo[0].app_id,
//     licenseQuantity: productCount.join(','),
//     serviceLocation: isGlobal ? SKU_SERVICE_LOCATION_WORLDWIDE : SKU_SERVICE_LOCATION_LOCAL,
//     addOns: keys,
//     expiry: getDateFormat(expiry)
//   }
//   return {
//     licenseCardInfo: licenseCardInfo,
//     addOns: [...addOnsGrouped],
//     validationRequest: validationRequest
//   }
// }
const getDateFormat = (dateValue) => {
  const [year, month, day] = dateValue.split('-')
  return `${month}/${day.slice(0, 2)}/${year}`
}
const addOnsGroupBy = (addOns) => {
  const result = []
  const addOnObject = groupBy(addOns, 'app_id')
  const keys = Object.keys(addOnObject)
  keys.length && keys.forEach((elem) => {
    let count = 0
    addOnObject[elem].forEach((network) => {
      if (addOnObject[elem].length > 1 && DUAL_NETWORKS_APP_IDS.has(elem)) {
        if (network.license_type.includes('-MU')) {
          network.formFieldName = network.formFieldName.concat(':', 'MU')
        }
        if (network.license_type.includes('-RN')) {
          network.formFieldName = network.formFieldName.concat(':', 'RN')
        }
        network.isDualNetwork = true
      }
      else {
        if (count > 0) {
          network.display = false
        }
        count++
      }
    })

    result.push(...addOnObject[elem])
  })
  return { keys, addOnsGrouped: result }
}

export const getUserCount = (licenseType, size) => {
  if (licenseType.toUpperCase().includes('MU-RN')) {
    return [`${size} Mobile Users`, `${size} Net Users`]
  }
  else if (licenseType.toUpperCase().includes('MU')) {
    return [`${size} Mobile Users`]

  }
  else if (licenseType.toUpperCase().toUpperCase().includes('RN')) {
    return [`${size} Net Users`]
  }
  else {
    return [`${size} Users`]
  }
}
export const ValidateSubDomainUrl = (request, tsg_id, app_id) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.validateUrl.post({ request, tsg_id, app_id }).then(({ data }) => {
      resolve(data.success ? VALIDATION_SUCCESS : VALIDATION_FAILED)
    }).catch((err) => {
      resolve(VALIDATION_FAILED)
    })
  })
}

export const LoadApps = (dispatch) => {
  const apps = getAuthState()?.apps
  if (apps?.length) {
    dispatch(loadApps(apps))
    return
  }
  actions.loadApps(TSG_NONE.id).then((data) => {
    dispatch(loadApps(data))
  }).catch(error => {
    dispatch(loadApps([]))
  })
}

export const GetValidation = (request, tsg_id, dispatch) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.prismaAccessValidation.post({ request, tsg_id }).then((response) => {
      resolve(groupBy(response.data.data, 'app_id'))
    }).catch((err) => {
      resolve({ status: 'FAILED' })
    })
  })
}

export const SubmitAllocation = (tsg_id, request, isUpdate = false) => {
  return new Promise((resolve, reject) => {
    if (!isUpdate) {
      SubscriptionService.submitAllocation.post({ request, tsg_id }).then(({ data }) => {
        resolve({ isSuccess: true, message: data })
      }).catch((err) => {
        reject({ isSuccess: false, message: err })
      })
    }
    else {
      SubscriptionService.submitAllocation.put({ request, tsg_id }).then(({ data }) => {
        resolve({ isSuccess: true, message: data })
      }).catch((err) => {
        reject({ isSuccess: false, message: err })
      })
    }
  })
}

export const CreateRootTSG = (request) => {
  return new Promise((resolve, reject) => {
    TenancyService.listTenant.post(request, TSG_NONE.id).then(({ data }) => {
      resolve(data)
    }).catch((err) => {
      reject({})
    })
  })
}

export const LoadRoles = (dispatch) => {
  const roles = getAuthState()?.rolePermissionDefinition?.roles
  if (roles.length) {
    dispatch(setRoles(roles))
    return
  }
  IAMService.roles.list(TSG_NONE.id).then(({ data }) => {
    dispatch(setRoles(data?.items))
  }).catch(error => {
    dispatch(setRoles([]))
  })
}

export const LoadPermissions = (dispatch) => {
  IAMService.permissions.list(TSG_NONE.id).then(({ data }) => {
    dispatch(setPermissions(data?.items))
  }).catch(error => {
    dispatch(setPermissions([]))
  })
}

export const LoadCustomRoles = (tsg_id, dispatch) => {
  return new Promise(((resolve, reject) => {
    IAMService.customRoles.list(tsg_id).then(({ data }) => {
      dispatch(setCustomRoles(data?.items))
      resolve(data.items)
    }).catch(error => {
      dispatch(setCustomRoles([]))
      reject(error)
    })
  }))
}

export const LoadPermissionSets = (dispatch) => {
  const permissionSets = getAuthState()?.rolePermissionDefinition?.permissionSets
  if (permissionSets.length) {
    dispatch(setPermissionSets(permissionSets))
    return
  }
  IAMService.permissionSets.list(TSG_NONE.id).then(({ data }) => {
    dispatch(setPermissionSets(data?.items))
  }).catch(() => {
    dispatch(setPermissionSets([]))
  })
}

export const LoadServiceAccounts = (tsg_id, dispatch) => {
  return new Promise((resolve, reject) => {
    return IAMService.serviceAccounts.list(tsg_id).then(({ data }) => {
      dispatch(setServiceAccounts(data?.items))
      resolve(data.items)
    }).catch(error => {
      dispatch(setServiceAccounts([]))
      reject(error)
    })
  })
}

export const LoadAccessPolicies = (tsg_id, dispatch) => {
  return new Promise((resolve, reject) => {
    return IAMService.accessPolicies.list(tsg_id).then(({ data }) => {
      dispatch(setAccessPolicies(data?.items))
      resolve(data.items)
    }).catch(error => {
      dispatch(setAccessPolicies([]))
      reject(error)
    })
  })
}

export const LoadDevices = (tsg_id, dispatch) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.devices.listTenantDevices({ tsg_id }).then(({ data }) => {
      // SubscriptionService.devices.list({ tsg_id }).then(({ data }) => {
      dispatch(loadDevices(Array.isArray(data) ? data : data?.items))
      resolve(Array.isArray(data) ? data : data?.items)
    }).catch(error => {
      dispatch([])
      reject(error)
    })
  })
}

export const LoadNotEligibleDASApps = (params) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.devices.listNotEligibleDASApps(params).then(({ data }) => {
      resolve(data)
    }).catch((err) => {
      reject(err)
    })
  })
}

export const LoadAppFirewallLicenses = ({ tsg_id, support_account_ids, app_id }, dispatch) => {
  return new Promise((resolve, reject) => {
    ActivationSubscriptionService.licenses.get({ tsg_id, support_account_ids, app_id }).then(({ data }) => {
      // dispatch(loadAppFirewallLicenses(Array.isArray(data) ? data : data?.items))
      resolve(Array.isArray(data) ? data : data?.items)
      // resolve(firewallLicenseMock)
    }).catch(error => {
      // dispatch([])
      reject(error)
      // resolve(firewallLicenseMock)
    })
  })
}


export const LoadCSPDevices = (support_account_id, x_sig, dispatch) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.devices.listSupportAccountDevices({
      tsg_id: TSG_NONE.id,
      support_account_id,
      x_sig
    }).then(({ data }) => {
      dispatch(loadCSPDevices(Array.isArray(data) ? data : data?.items))
      resolve(Array.isArray(data) ? data : data?.items)
    }).catch(error => {
      dispatch([])
      reject(error)
    })
  })
}

export const SubmitSubscriptionModification = (tsg_id, request) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.modifySubscription.post({ request, tsg_id }).then(({ data }) => {
      resolve({ isSuccess: true, message: data })
    }).catch((err) => {
      reject(err)
    })
  })
}
export const amendSubscriptionClaim = (tsg_id, request) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.listLicense.put(request, tsg_id).then(({ data }) => {
      resolve({ isSuccess: true, response: data })
    }, (err) => {
      resolve({ isSuccess: false })
    })
  })
}
export const FetchCSPAccounts = (params) => {
  return new Promise((resolve, reject) => {
    SubscriptionService.listCspAccounts.get(params).then(({ data }) => {
      resolve(data)
    }).catch((err) => {
      resolve([])
    })
  })
}
export const LoadSKUCompatibleDevices = ({ tsg_id, license_skus }, dispatch) => {
  return new Promise((resolve, reject) => {
    ActivationSubscriptionService.devices.get({ tsg_id, license_skus }).then(({ data }) => {
      // dispatch(loadAppFirewallLicenses(Array.isArray(data) ? data : data?.items))
      resolve(Array.isArray(data) ? data : data?.items)
      // resolve(firewallLicenseMock)
    }).catch(error => {
      // dispatch([])
      reject(error)
      // resolve(firewallLicenseMock)
    })
  })
}
export const LoadDeviceLicenses = ({ tsg_id, serial_numbers }, dispatch) => {
  return new Promise((resolve, reject) => {
    ActivationSubscriptionService.devices.licenses({ tsg_id, serial_numbers }).then(({ data }) => {
      // dispatch(loadAppFirewallLicenses(Array.isArray(data) ? data : data?.items))
      resolve(Array.isArray(data) ? data : data?.items)
      // resolve(firewallLicenseMock)
    }).catch(error => {
      // dispatch([])
      reject(error)
      // resolve(firewallLicenseMock)
    })
  })
}

export const LoadAcquisitions = (tsg_id, dispatch) => {
  return new Promise((resolve, reject) => {
    TenancyService.acquisitions.list(tsg_id)
      .then(({ data }) => {
        dispatch(loadAcquisitions(data.items))
        resolve(data.items)
      })
      .catch((error) => {
        dispatch(loadAcquisitions([]))
        reject(error)
      })
  })
}

export const LoadAcqRequests = (tsg_id, dispatch) => {
  return new Promise((resolve, reject) => {
    TenancyService.acquisitionRequests.list(tsg_id)
      .then(({ data }) => {
        dispatch(loadAcqRequests(data.items))
        resolve(data.items)
      })
      .catch((error) => {
        dispatch(loadAcqRequests([]))
        reject(error)
      })
  })
}

export const LoadAcqList = (tsg_id, dispatch) => {
  dispatch(setAcqLoading(true))
  dispatch(loadAcquisitions([]))
  dispatch(loadAcqRequests([]))
  return Promise.all([
    LoadAcquisitions(tsg_id, dispatch),
    LoadAcqRequests(tsg_id, dispatch)
  ]).finally(() => dispatch(setAcqLoading(false)))
}
